const MapTypes = Object.freeze({
  VEHICLE: 'vehicles',
  ROUTE: 'routes',
  CHARGEPOINT: 'chargepoints',
});

const MarkerLayers = Object.freeze({
  TRUCK: {
    image: '/assets/images/truck.png',
    events: {
      mouseenter: handleMouseEnter,
      mouseleave: handleMouseLeave,
      click: handleClick,
    },
    extraLayers: ['TRUCK-circle-large-0.75', 'TRUCK-circle-small-0.25'],
  },
  CHARGEPOINT: {
    image: '/assets/images/chargepoint.png',
    events: {
      mouseenter: handleMouseEnter,
      mouseleave: handleMouseLeave,
      click: handleClick,
    },
  },
});

// Generic mouse event handlers
function handleMouseEnter(e) {
  if (this.active.marker) return;
  this.setActiveMarker(e.features[0]);
}

function handleMouseLeave(e) {
  if (this.active.popup?.isOpen()) return;
  this.removeActiveMarker();
}

function handleClick(e) {
  this.pointClickHandler(e);
}

export default class MapMarkers {
  constructor(parent) {
    this.parent = parent; // Reference to the map web component
    this.map = parent.map;
    this.markers = [];
    this.truckMarkers = new Map(); // Map<featureId, Marker>

    this.active = {
      popup: null,
      marker: null,
    };

    this.init();
  }

  removeActiveMarker() {
    this.active.marker?.remove();
    this.active.marker = null;
  }

  setActiveMarker(feature) {
    this.removeActiveMarker();
    const div = document.createElement('div');
    div.innerHTML = feature.properties.marker;
    div.style.pointerEvents = 'none';
    this.active.marker = new mapboxgl.Marker(div).setLngLat(feature.geometry.coordinates).addTo(this.map);
  }

  pointClickHandler(e) {
    const layerId = e.features[0].layer.id;
    const popupOffset = e.features[0].properties?.popupOffset || 0;

    e.preventDefault();

    switch (e.features.length > 1 ? 'clusters' : layerId) {
      case 'clusters':
        const clickedPoint = e.features[0];
        const allFeatures = this.map.querySourceFeatures('markers');

        const matchingFeatures = Object.values(
          allFeatures.reduce((mapped, feature) => {
            const coords = feature.geometry.coordinates;
            if (coords[0] === clickedPoint.geometry.coordinates[0] && coords[1] === clickedPoint.geometry.coordinates[1]) {
              mapped[feature.properties.id] = feature;
            }
            return mapped;
          }, {})
        );

        var coordinates = matchingFeatures[0].geometry.coordinates.slice();
        var descriptions = matchingFeatures.map((feature) => feature.properties.description).join('');
        var descriptions = `
          <div class="overflow-auto max-h-64">
            ${descriptions}
          </div>
        `;

        this.active.popup = new mapboxgl.Popup({
          maxWidth: '700px',
          offset: popupOffset,
        })
          .setLngLat(coordinates)
          .setHTML(descriptions)
          .addTo(this.map);
        break;
      default:
        var feature = e.features[0];
        var coordinates = feature.geometry.coordinates.slice();
        var description = feature.properties.description;

        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }

        this.setActiveMarker(feature);

        this.active.popup = new mapboxgl.Popup({
          maxWidth: '700px',
          offset: popupOffset,
        })
          .setLngLat(coordinates)
          .setHTML(description)
          .addTo(this.map);

        if (layerId === 'TRUCK' || layerId === 'CHARGEPOINT') {
          this.active.popup.on('close', this.removeActiveMarker.bind(this));
        }
    }
  }

  init() {
    this.map.on('click', 'clusters', this.pointClickHandler.bind(this));
    this.map.on('click', 'unclustered-point', this.pointClickHandler.bind(this));

    this.map.on('mouseenter', 'clusters', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });
    this.map.on('mouseleave', 'clusters', () => {
      this.map.getCanvas().style.cursor = '';
    });

    // Load images and add layers
    for (const key in MarkerLayers) {
      const iconData = MarkerLayers[key];
      for (const event in iconData.events) {
        this.map.on(event, key, iconData.events[event].bind(this));
      }
      this.map.loadImage(iconData.image, (error, image) => {
        if (error) {
          console.error(`Error loading image ${iconData.image}:`, error);
          return;
        }
        this.parent.ready.then(() => {
          if (!this.map.hasImage(key)) this.map.addImage(key, image);
        });
      });
    }

    this.map.on('sourcedata', (e) => {
      // When markers source is added for the first time
      if (e.sourceId === 'markers' && e.isSourceLoaded && e.sourceDataType == 'metadata' && !this.map.getLayer('clusters')) {
        this.map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'markers',
          filter: ['all', ['>', 'point_count', 1], ['!has', 'icon']],
          paint: {
            'circle-color': [
              'step',
              ['get', 'point_count'],
              '#000', // small cluster
              100,
              '#000', // medium cluster
              750,
              '#000', // large cluster
            ],
            'circle-radius': {
              base: 2,
              stops: [
                [12, 4],
                [22, 180],
              ],
            },
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });

        this.map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'markers',
          filter: ['all', ['>', 'point_count', 1], ['!has', 'icon']],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-size': 10,
          },
          paint: {
            'text-color': '#fff', // White text for cluster count
          },
        });

        // Circle layer for unclustered points
        this.map.addLayer({
          id: 'unclustered-point',
          type: 'circle',
          source: 'markers',
          filter: ['all', ['any', ['!has', 'point_count'], ['<=', 'point_count', 1]], ['!has', 'icon']],
          paint: {
            'circle-color': '#000000',
            'circle-radius': {
              base: 2,
              stops: [
                [12, 4],
                [22, 180],
              ],
            },
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });

        for (const key in MarkerLayers) {
          if (key === 'TRUCK') {
            for (const [opacity, size] of [
              [0.75, 'large'],
              [0.25, 'small'],
            ]) {
              this.map.addLayer({
                id: `TRUCK-circle-${size}-${opacity}`,
                type: 'circle',
                source: 'markers',
                filter: ['==', 'icon', 'TRUCK'],
                paint: {
                  'circle-color': [
                    'match',
                    ['get', 'status', ['get', 'extra']],
                    'CHARGING',
                    `rgba(0, 149, 255, ${opacity})`, // blue
                    'CHARGING_AC',
                    `rgba(0, 149, 255, ${opacity})`, // blue
                    'CHARGING_DC',
                    `rgba(0, 149, 255, ${opacity})`, // blue
                    'ERROR',
                    `rgba(238, 98, 98, ${opacity})`, // red
                    /* other */ `rgba(65, 184, 112, ${opacity})`, // green
                  ],
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    ...(size === 'large' ? [0, 15, 22, 15] : [0, 25, 32, 25]), // Outer circle radius at zoom level 0 and 22
                  ],
                },
              });
            }

            // Handle TRUCK icons separately
            this.map.addLayer({
              id: 'TRUCK', // Layer for TRUCK icons
              type: 'symbol',
              source: 'markers',
              filter: ['==', 'icon', 'TRUCK'],
              layout: {
                'icon-image': 'TRUCK',
                'icon-rotate': ['get', 'rotation'],
                'icon-size': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  0,
                  0.01, // Size at zoom level 0 for TRUCK
                  8,
                  0.06, // Size at zoom level 8 for TRUCK
                ],
                'icon-allow-overlap': true,
              },
            });
            console.log(`Marker layer added: TRUCK`);
          }
          if (key !== 'TRUCK') {
            this.map.addLayer({
              id: key,
              type: 'symbol',
              source: 'markers',
              filter: ['==', 'icon', key],
              layout: {
                'icon-image': key,
                'icon-rotate': ['get', 'rotation'],
                'icon-allow-overlap': true,
                'icon-offset': [0, -15], // Offset for other icons
              },
              paint: {
                'text-color': '#fff', // White text for cluster count
              },
            });
            console.log(`Marker layer added: ${key}`);
          }
        }
      }
    });
  }

  get cluster() {
    return this.parent.cluster;
  }

  updateOrCreateSource(name, data) {
    if (this.map.getSource(name)) {
      this.map.getSource(name).setData(data);
    } else {
      this.map.addSource(name, {
        type: 'geojson',
        data: data,
      });
    }
  }

  addRouteMarkers() {
    // Adding route markers logic
    const chargePointMarkers = this.markers.filter((m) => m.properties.type === 'Chargepoint');
    const routeMarkers = this.markers.filter((m) => m.properties.type !== 'Chargepoint');
    routeMarkers.forEach((feature) => {
      const div = document.createElement('div');
      div.className = 'w-[8px] h-[8px] rounded-full bg-black route-marker';
      let html = '';

      if (feature.properties.type === 'via') {
        div.className = 'w-[20px] h-[20px] rounded-full bg-white border-[2px] border-blue-800 route-marker flex justify-center items-center';
        html += `<span class="font-bold">${feature.properties.sequence}</span>`;
      } else {
        const offset = feature.properties.type === 'Start' ? '-left-11' : '-left-10';
        html += `<div class="absolute ${offset} top-1/2 transform -translate-y-1/2 bg-black px-1 text-white uppercase whitespace-nowrap h-4 flex items-center text-sm font-medium arrow overlay" style="font-size: 8px;">${feature.properties.type}</div>`;
      }
      div.innerHTML = html;

      const description = feature.properties.description;
      const routeId = feature.properties.id.replace(/_.*$/, '');
      const sameRoutes = this.markers.filter((f) => f.properties.id.replace(/_.*$/, '') === routeId);
      const startRoute = this.markers.find((f) => f.properties.id === routeId + '_from');

      let finalDescription = description;
      if (startRoute) {
        const content = sameRoutes.map((f) => f.properties.description.replace(/type="[^"]+"/, `type="route-step" sequence="${f.properties.sequence}" step-type="${f.properties.type}"`)).join('');
        finalDescription = `${startRoute.properties.description}<div class="px-3.5 pt-4 overflow-y-auto max-h-96" style="max-height: 50vh;">${content}</div>`;
      }

      const popup = new mapboxgl.Popup({ maxWidth: '317px' }).setLngLat(feature.geometry.coordinates).setHTML(finalDescription);
      const marker = new mapboxgl.Marker(div).setLngLat(feature.geometry.coordinates).setPopup(popup).addTo(this.map);
      this.truckMarkers.set(feature.properties.id, marker);
    });
    this.updateOrCreateSource('markers', {
      type: 'FeatureCollection',
      features: chargePointMarkers,
    });
  }

  addMarkers() {
    this.updateOrCreateSource('markers', {
      type: 'FeatureCollection',
      features: this.markers,
    });
  }

  countMarkers(marker, increment = true) {
    // Use a map for efficient counting
    const key = `${marker.coordinates[0]}-${marker.coordinates[1]}`;
    if (!this.markerCounts) this.markerCounts = new Map();
    if (increment) {
      this.markerCounts.set(key, (this.markerCounts.get(key) || 0) + 1);
    }
    return this.markerCounts.get(key);
  }

  /**
   * Convert raw marker data to GeoJSON feature.
   * @param {Object} m - Raw marker data
   * @returns {Object} GeoJSON feature
   */
  _toFeature(m) {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: m.coordinates,
      },
      properties: {
        id: m.id,
        icon: m.icon || undefined,
        rotation: m.rotation ?? 0,
        description: m.popup || '',
        marker: m.marker || '',
        type: m.type || '',
        sequence: m.sequence || 0,
        extra: m.extra || {},
        point_count: this.countMarkers(m, false),
        popupOffset: m.layout?.popupOffset || undefined,
      },
    };
  }

  // Reuse the GeoJSON source and layers when refreshing live vehicles.
  replaceMarkers(markers) {
    this.removeActiveMarker();
    this.active.popup?.remove();
    this.active.popup = null;
    this.markerCounts = new Map();
    if (this.cluster) {
      for (const marker of markers) this.countMarkers(marker);
    }
    this.markers = markers.map((marker) => this._toFeature(marker));
    if (this.markers.length || this.map.getSource('markers')) {
      this.addMarkers();
    }
  }

  add(markers) {
    if (markers.length === 0) return;

    if (this.cluster) {
      for (const marker of markers) {
        this.countMarkers(marker);
      }
    }

    this.markers = markers.map((m) => this._toFeature(m));

    console.log(`Adding markers:`, this.markers);
    this.parent.type === MapTypes.ROUTE ? this.addRouteMarkers() : this.addMarkers();
  }

  /**
   * Append markers to existing markers and update the map.
   * @param {Array} markers - Array of raw marker data to append
   */
  appendMarkers(markers) {
    if (markers.length === 0) return;

    if (this.cluster) {
      for (const marker of markers) {
        this.countMarkers(marker);
      }
    }

    const newFeatures = markers.map((m) => this._toFeature(m));
    this.markers = [...this.markers, ...newFeatures];

    if (this.parent.type === MapTypes.ROUTE) {
      // For routes, create DOM markers for route markers and update source for chargepoints
      this._appendRouteMarkers(newFeatures);
    } else {
      // For non-routes, just update the source
      this.updateOrCreateSource('markers', {
        type: 'FeatureCollection',
        features: this.markers,
      });
    }
  }

  /**
   * Append route markers (creates DOM markers for new route markers).
   * @param {Array} newFeatures - New GeoJSON features to add
   */
  _appendRouteMarkers(newFeatures) {
    const newChargePointMarkers = newFeatures.filter((m) => m.properties.type === 'Chargepoint');
    const newRouteMarkers = newFeatures.filter((m) => m.properties.type !== 'Chargepoint');

    // Create DOM markers for new route markers
    newRouteMarkers.forEach((feature) => {
      const div = document.createElement('div');
      div.className = 'w-[8px] h-[8px] rounded-full bg-black route-marker';
      let html = '';

      if (feature.properties.type === 'via') {
        div.className = 'w-[20px] h-[20px] rounded-full bg-white border-[2px] border-blue-800 route-marker flex justify-center items-center';
        html += `<span class="font-bold">${feature.properties.sequence}</span>`;
      } else {
        const offset = feature.properties.type === 'Start' ? '-left-11' : '-left-10';
        html += `<div class="absolute ${offset} top-1/2 transform -translate-y-1/2 bg-black px-1 text-white uppercase whitespace-nowrap h-4 flex items-center text-sm font-medium arrow overlay" style="font-size: 8px;">${feature.properties.type}</div>`;
      }
      div.innerHTML = html;

      const description = feature.properties.description;
      const routeId = feature.properties.id.replace(/_.*$/, '');
      const sameRoutes = this.markers.filter((f) => f.properties.id.replace(/_.*$/, '') === routeId);
      const startRoute = this.markers.find((f) => f.properties.id === routeId + '_from');

      let finalDescription = description;
      if (startRoute) {
        const content = sameRoutes.map((f) => f.properties.description.replace(/type="[^"]+"/, `type="route-step" sequence="${f.properties.sequence}" step-type="${f.properties.type}"`)).join('');
        finalDescription = `${startRoute.properties.description}<div class="px-3.5 pt-4 overflow-y-auto max-h-96" style="max-height: 50vh;">${content}</div>`;
      }

      const popup = new mapboxgl.Popup({ maxWidth: '317px' }).setLngLat(feature.geometry.coordinates).setHTML(finalDescription);
      const marker = new mapboxgl.Marker(div).setLngLat(feature.geometry.coordinates).setPopup(popup).addTo(this.map);
      this.truckMarkers.set(feature.properties.id, marker);
    });

    // Update source with all chargepoint markers
    const allChargePointMarkers = this.markers.filter((m) => m.properties.type === 'Chargepoint');
    this.updateOrCreateSource('markers', {
      type: 'FeatureCollection',
      features: allChargePointMarkers,
    });
  }

  clear() {
    if (!this.map?.style) return;

    for (const key in MarkerLayers) {
      if (this.map.getLayer(key)) this.map.removeLayer(key);
      for (const extraLayer of MarkerLayers[key].extraLayers || []) {
        if (this.map.getLayer(extraLayer)) this.map.removeLayer(extraLayer);
      }
    }

    ['clusters', 'cluster-count', 'unclustered-point'].forEach((layer) => {
      if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    });

    if (this.map.getSource('markers')) {
      this.map.removeSource('markers');
    }

    this.truckMarkers.forEach((marker) => marker.remove());
    this.truckMarkers = new Map();
    this.markers = [];
    this.markerCounts = new Map();
  }

  /**
   * Remove markers whose ID starts with a given prefix.
   * @param {string} prefix - The prefix to match against marker IDs
   */
  removeByPrefix(prefix) {
    if (!this.map?.style) return;

    const beforeCount = this.markers.length;
    this.markers = this.markers.filter((m) => !m.properties.id.startsWith(prefix));

    // Remove DOM markers (truckMarkers) that match the prefix
    for (const [id, marker] of this.truckMarkers) {
      if (id.startsWith(prefix)) {
        marker.remove();
        this.truckMarkers.delete(id);
      }
    }

    if (this.markers.length !== beforeCount && this.map.getSource('markers')) {
      // For routes, source only contains chargepoint markers
      const sourceFeatures = this.parent.type === MapTypes.ROUTE
        ? this.markers.filter((m) => m.properties.type === 'Chargepoint')
        : this.markers;
      this.map.getSource('markers').setData({
        type: 'FeatureCollection',
        features: sourceFeatures,
      });
    }
  }
}
